<script setup>
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors';
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import { useRouter } from "vue-router";
import BaseTable from "@/components/tables/BaseTable.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import { getAvailableActionsForRoute } from "@/services/api/permissionManagement.js";
import { QrcodeLogoEntity } from "@/entities/QrcodeLogoEntity.js";
import { listLogos, deleteLogo } from "@/services/api/qrcode.js";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";

const { t } = useI18n();
const router = useRouter();
const entity = ref(new QrcodeLogoEntity());
const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const actions = ref([]);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null)
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);

const orderBy = ref(null);

async function fetchData(params = {}) {
	try {
		loading.value = true;
		currentPage.value = params.page ?? currentPage.value;
		if (orderBy.value) params['orderBy'] = orderBy.value.key;
		const response = await listLogos({ limit: itemsPerPage.value, page: currentPage.value, ...params });
		items.value = response.data.items ?? response.data;
		totalItems.value = response.page;
	}
	catch (error) { console.error(error); message.error(await handleError(error, t)); }
	finally { loading.value = false; }
}
function onEdit(item) { router.push(entity.value.pages.edit.route?.at(-1).replace(':id', item.id)); }
function onDelete(item) { itemToDelete.value = item; showDialogConfirmDelet.value = true; }
async function confirmDelete() { try { await deleteLogo(itemToDelete.value.id); await fetchData(); message.success(t('common.messages.success.itemDeletedSuccessfully')); showDialogConfirmDelet.value = false; } catch (e) { message.error(await handleError(e, t)); } }
async function onPageChange(page) { await fetchData({ page }); }
async function onOrderBy(o) { orderBy.value = o; }

onMounted(async () => {
	actions.value = await getAvailableActionsForRoute(entity.value.APIRoute);
	await fetchData();
});
</script>

<template>
	<BasePage :entity="entity">
		<template #default>
			<v-row class="mb-4">
				<v-col>
					<ButtonNew :route="entity.pages.create.route?.at(-1)" />
				</v-col>
				<v-col class="base-quick-search">
					<BaseQuickSearch style="width:100%" :entity="entity" v-if="quickSearchConfig"
						:config="quickSearchConfig" v-model="searchText" :fetchData="fetchData" />
				</v-col>
			</v-row>
			<BaseTable :fetchData="fetchData" :entity="entity" :items="items" :loading="loading"
				:totalItems="totalItems" :currentPage="currentPage" :itemsPerPage="itemsPerPage" :actions="actions"
				@edit="onEdit" @delete="onDelete" @page-change="onPageChange" @onOrderBy="onOrderBy" />
			<DialogConfirmDelet v-model="showDialogConfirmDelet" :item="itemToDelete?.name" @confirm="confirmDelete" />
		</template>
	</BasePage>
</template>
